import { z } from 'zod';
import { TitleOwnerVerificationReasonCode } from '../types';

export const TITLE_OWNER_DOCUMENT_VERIFICATION_TIMEOUT_MS = 5 * 60 * 1_000;

/**
 * CT-3015: shape guard for `PropertyTitleOwner.incomeVerificationDetails`
 * (a Prisma `Json?` column). A discriminated union keyed by `method`, mirroring
 * the domain classes in
 * `services/src/models/property-title-search/models/income-verification-details.model.ts`.
 * The service layer `safeParse`s the value against this before every write, so
 * a malformed payload never reaches the database.
 */

export const incomeVerificationNameMatchSchema = z.object({
  status: z.enum(['PASS', 'FLAG', 'SKIP']),
  confidence: z.number().nullable(),
});

// Per-owner reason code, shared across every method variant. `.nullish()` so
// clean verifications (null) and older records written before this field existed
// (undefined) both parse; a present value is one of the enum members.
const reasonCodeSchema = z.nativeEnum(TitleOwnerVerificationReasonCode).nullish();

const verifiedTitleOwnerDocumentResultSchema = z.object({
  type: z.string(),
  documentId: z.string(),
  validation: z.string(),
  reason: z.string(),
  failureCode: z.string().optional(),
});

export const asyncTitleOwnerDocumentVerificationSchema = z.object({
  requestId: z.string().min(1),
  // Optional for compatibility with requests written before stale-request
  // recovery was introduced. The owner row's updated timestamp is the fallback.
  requestedAt: z.string().datetime().optional(),
  documentIds: z.array(z.string().min(1)).min(1),
  result: z
    .object({
      validation: z.string(),
      numberOfDocumentsPassed: z.number().int().nonnegative(),
      statusCode: z.string().optional(),
      documents: z.array(verifiedTitleOwnerDocumentResultSchema),
    })
    .nullable(),
});

// method: 'NOA' — exactly one accepted NOA. The monthly income lives on the
// `aiIncomeCalculated` column; only the accepted FY + name match are recorded.
export const noaVerificationDetailsSchema = z.object({
  method: z.literal('NOA'),
  // `null` = no NOA passed (failed / $0 / out-of-window) — a valid recorded state.
  // An empty string is NOT valid: when a year IS captured it must be non-empty
  // (e.g. '2023-2024'). Hence `.min(1).nullable()` rather than allowing ''.
  financialYear: z.string().min(1).nullable(),
  nameMatch: incomeVerificationNameMatchSchema.nullable(),
  reasonCode: reasonCodeSchema,
  asyncVerification: asyncTitleOwnerDocumentVerificationSchema.optional(),
});

// method: 'SUPERANNUATION' — observe-only. Figures are recorded but never gated on.
export const superannuationVerificationDetailsSchema = z.object({
  method: z.literal('SUPERANNUATION'),
  classification: z.string(),
  confidence: z.string(),
  provider: z.string().nullable(),
  paymentAmount: z.number().nullable(),
  frequency: z.string().nullable(),
  monthlyIncome: z.number().nullable(),
  notes: z.string().nullable(),
  reasonCode: reasonCodeSchema,
  asyncVerification: asyncTitleOwnerDocumentVerificationSchema.optional(),
});

// Pensioner Concession Card. `customerReferenceNumber` is sensitive PII —
// persisted server-side for the Equifax call only, redacted by the repository on
// read, never exposed over GraphQL, and MUST never be logged.
export const concessionCardSchema = z.object({
  cardType: z.literal('PCC'),
  nameOnCard: z.string().min(1),
  dateOfExpiry: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'dateOfExpiry must be YYYY-MM-DD'),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'dateOfBirth must be YYYY-MM-DD'),
  customerReferenceNumber: z.string().min(1),
});

// method: 'IDMATRIX' — pensioner concession-card path. `idMatrixReference` +
// `overallOutcome` + `matchScore` are only populated when the LaunchDarkly flag
// is ON and Equifax was actually called; with the flag OFF the card is captured
// but Equifax is skipped, so those fields stay null (owner is FAILED / referred).
export const idMatrixVerificationDetailsSchema = z.object({
  method: z.literal('IDMATRIX'),
  idMatrixReference: z.string().nullable(),
  overallOutcome: z.enum(['ACCEPT', 'REJECT', 'TIMEOUT', 'ERROR']).nullable(),
  matchScore: z.number().nullable(),
  concessionCard: concessionCardSchema,
  reasonCode: reasonCodeSchema,
});

// method: 'NO_INCOME' — the owner declared they have no income.
export const noIncomeVerificationDetailsSchema = z.object({
  method: z.literal('NO_INCOME'),
  hasNoIncome: z.boolean(),
  noaProvided: z.boolean(),
  reasonCode: reasonCodeSchema,
});

// method: 'NONE' — proceeded without a document and without a no-income
// declaration. Allowed through, recorded as FAILED with a reason.
export const noDocumentVerificationDetailsSchema = z.object({
  method: z.literal('NONE'),
  reason: z.string(),
  reasonCode: reasonCodeSchema,
});

export const incomeVerificationDetailsSchema = z.discriminatedUnion('method', [
  noaVerificationDetailsSchema,
  superannuationVerificationDetailsSchema,
  idMatrixVerificationDetailsSchema,
  noIncomeVerificationDetailsSchema,
  noDocumentVerificationDetailsSchema,
]);

export type IncomeVerificationDetailsInput = z.infer<typeof incomeVerificationDetailsSchema>;
